import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Phone, Mail, Search, BookUser, Smartphone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";
import MobileInputWithWA from "@/components/MobileInputWithWA";

const emptyForm = {
  surname: "", name: "", role: "", company: "",
  phone: "", mobile: "", email: "", address: "", notes: "", category: "altro",
};

const CATEGORIES = [
  { v: "broker", l: "Broker / Agenzia", color: "bg-blue-50 border-blue-200 text-blue-700" },
  { v: "compagnia", l: "Compagnia", color: "bg-violet-50 border-violet-200 text-violet-700" },
  { v: "fornitore", l: "Fornitore", color: "bg-amber-50 border-amber-200 text-amber-700" },
  { v: "consulente", l: "Consulente", color: "bg-emerald-50 border-emerald-200 text-emerald-700" },
  { v: "altro", l: "Altro", color: "bg-slate-50 border-slate-200 text-slate-700" },
];
const catColor = (c) => CATEGORIES.find((x) => x.v === c)?.color || CATEGORIES[4].color;
const catLabel = (c) => CATEGORIES.find((x) => x.v === c)?.l || "Altro";

export default function PhoneDirectory() {
  const { canWrite } = usePermissions();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("__all__");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/phone-directory");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...emptyForm, ...c }); setOpen(true); };

  const submit = async () => {
    try {
      if (editing) await api.put(`/phone-directory/${editing.id}`, form);
      else await api.post("/phone-directory", form);
      toast.success(editing ? "Contatto aggiornato" : "Contatto aggiunto");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/phone-directory/${deleteTarget.id}`);
      toast.success("Contatto eliminato");
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = useMemo(() => {
    return items.filter((c) => {
      if (filterCat !== "__all__" && (c.category || "altro") !== filterCat) return false;
      if (!q) return true;
      const t = q.toLowerCase();
      return [c.surname, c.name, c.role, c.company, c.phone, c.mobile, c.email, c.notes]
        .some((v) => (v || "").toLowerCase().includes(t));
    });
  }, [items, q, filterCat]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="phone-directory-page">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Uso interno</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">📞 Rubrica telefonica</h1>
          <p className="text-slate-500 mt-1">{items.length} contatti totali · uso esclusivo dello staff</p>
        </div>
        {canWrite("categories") && (
          <Button data-testid="add-contact-btn" onClick={openCreate} className="bg-[#0F172A] hover:bg-[#1E293B]">
            <Plus className="h-4 w-4 mr-2" /> Nuovo contatto
          </Button>
        )}
      </header>

      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]">
          <Label className="text-xs">Cerca</Label>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, cognome, telefono, azienda…" className="pl-8" />
          </div>
        </div>
        <div className="min-w-[180px]">
          <Label className="text-xs">Categoria</Label>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger data-testid="directory-cat"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutte</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-slate-500 ml-auto">{filtered.length} di {items.length}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.length === 0 ? (
          <div className="col-span-full bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-400 text-sm">
            <BookUser className="h-10 w-10 mx-auto text-slate-300 mb-2" />
            Nessun contatto trovato.
          </div>
        ) : filtered.map((c) => (
          <div key={c.id} data-testid={`contact-${c.id}`} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold text-base text-slate-900 truncate uppercase">{c.surname} {c.name}</div>
                {(c.role || c.company) && (
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{[c.role, c.company].filter(Boolean).join(" · ")}</p>
                )}
                <span className={`inline-block mt-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${catColor(c.category)}`}>
                  {catLabel(c.category)}
                </span>
              </div>
              {canWrite("categories") && (
                <div className="flex items-center gap-1 text-slate-400">
                  <button onClick={() => openEdit(c)} data-testid={`edit-${c.id}`} className="hover:text-blue-600 p-1"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => setDeleteTarget(c)} data-testid={`delete-${c.id}`} className="hover:text-red-600 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              )}
            </div>
            <div className="mt-3 space-y-1 text-sm">
              {c.phone && (
                <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-600">
                  <Phone className="h-3.5 w-3.5 text-slate-400" /><span className="tabular-nums">{c.phone}</span>
                </a>
              )}
              {c.mobile && (
                <a href={`tel:${c.mobile}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-600">
                  <Smartphone className="h-3.5 w-3.5 text-slate-400" /><span className="tabular-nums">{c.mobile}</span>
                </a>
              )}
              {c.email && (
                <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-600 truncate">
                  <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" /><span className="truncate">{c.email}</span>
                </a>
              )}
              {c.address && (
                <div className="flex items-start gap-2 text-slate-600">
                  <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" /><span className="text-xs">{c.address}</span>
                </div>
              )}
              {c.notes && (
                <p className="text-xs text-slate-500 italic mt-1.5 pt-1.5 border-t border-slate-100 line-clamp-3">{c.notes}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Dialog create/edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookUser className="h-5 w-5 text-blue-600" />
              {editing ? "Modifica contatto" : "Nuovo contatto rubrica"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Cognome *</Label>
              <Input data-testid="ct-surname" value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} className="uppercase" />
            </div>
            <div>
              <Label>Nome</Label>
              <Input data-testid="ct-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="uppercase" />
            </div>
            <div>
              <Label>Ruolo / Mansione</Label>
              <Input data-testid="ct-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="es. Agente, Broker, Tecnico" />
            </div>
            <div>
              <Label>Azienda / Agenzia</Label>
              <Input data-testid="ct-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>
            <div>
              <Label>Telefono fisso</Label>
              <Input data-testid="ct-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="es. 091 1234567" />
            </div>
            <div>
              <Label>Cellulare</Label>
              <MobileInputWithWA
                value={form.mobile}
                onChange={(v) => setForm({ ...form, mobile: v })}
                testid="ct-mobile"
                clientName={`${form.name || ""} ${form.surname || ""}`.trim()}
                placeholder="es. +39 333 1234567"
              />
            </div>
            <div className="col-span-2">
              <Label>Email</Label>
              <Input data-testid="ct-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Indirizzo</Label>
              <Input data-testid="ct-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Via, città, CAP" />
            </div>
            <div>
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="ct-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Note</Label>
              <Textarea data-testid="ct-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Promemoria, orari, referente, ecc." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-contact" disabled={!form.surname.trim()} onClick={submit} className="bg-[#0F172A] hover:bg-[#1E293B]">
              {editing ? "Salva" : "Aggiungi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma delete */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="confirm-delete-contact">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" /> Eliminare il contatto?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700">
            <p>Stai per eliminare:</p>
            <p className="font-semibold bg-slate-50 border border-slate-200 rounded px-3 py-2 mt-2 uppercase">{deleteTarget?.surname} {deleteTarget?.name}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annulla</Button>
            <Button onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
